import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LanguageIcon from '@mui/icons-material/Language';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useAnalyzeWebsiteMutation } from '../../services/api/setupOnboarding.api';
import { OnboardingWebsiteAnalysis } from '../../services/types/onboarding';
import styles from './SetupOnboarding.module.scss';
import WebsiteAnalysisResults from './WebsiteAnalysisResults';
import { addHTTPSProtocolToUrl, validateUrl } from './utils';

const SetupOnboarding: React.FC = () => {
  const navigate = useNavigate();

  const [companyUrl, setCompanyUrl] = useState('');
  const [errors, setErrors] = useState<{ companyUrl?: string }>({});

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<OnboardingWebsiteAnalysis | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const [analyzeWebsite] = useAnalyzeWebsiteMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setErrors({});
    setAnalysisError(null);

    const normalizedUrl = addHTTPSProtocolToUrl(companyUrl.trim());

    if (!normalizedUrl) {
      setErrors({ companyUrl: 'La URL de la empresa es requerida' });
      return;
    }

    if (!validateUrl(normalizedUrl)) {
      setErrors({ companyUrl: 'Por favor ingresa una URL válida (ej: https://miempresa.com)' });
      return;
    }

    setIsAnalyzing(true);

    try {
      const result = await analyzeWebsite({ baseUrl: normalizedUrl }).unwrap();
      setAnalysisResult(result);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error analyzing website:', error);
      setAnalysisError('Error al analizar el sitio web. Por favor, inténtalo de nuevo.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCompanyUrl(value);

    if (errors.companyUrl) {
      setErrors({ ...errors, companyUrl: undefined });
    }
  };

  const handleBackToForm = () => {
    setAnalysisResult(null);
    setAnalysisError(null);
  };

  const handleContinueToConfigureLinks = () => {
    navigate('/setup-onboarding/configure-links', {
      state: { analysisResult },
    });
  };

  if (analysisResult) {
    return (
      <div className={styles.setupContainer}>
        <div className={styles.setupContent}>
          <Box className={styles.header}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToForm}
              variant="outlined"
              className={styles.backButton}
              sx={{ alignSelf: 'flex-start', mb: 2 }}
            >
              Volver Atrás
            </Button>
          </Box>

          <WebsiteAnalysisResults analysis={analysisResult} onContinue={handleContinueToConfigureLinks} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.setupContainer}>
      <div className={styles.setupContent}>
        {/* Header */}
        <Box className={styles.header}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            variant="outlined"
            className={styles.backButton}
            sx={{ alignSelf: 'flex-start', mb: 2 }}
          >
            Volver al Dashboard
          </Button>

          <Typography variant="h4" component="h1" className={styles.title}>
            ¡Bienvenido a Zeller! 🚗
          </Typography>
          <Typography variant="subtitle1" className={styles.subtitle}>
            Configuremos tu concesionario para comenzar
          </Typography>
        </Box>

        {/* Progress indicator */}
        <Box className={styles.progressSection}>
          <Typography variant="body2" className={styles.progressLabel}>
            Paso 1 de 3
          </Typography>
          <LinearProgress variant="determinate" value={33} className={styles.progressBar} />
        </Box>

        {/* Loading state */}
        {isAnalyzing && (
          <Card className={styles.loadingCard}>
            <CardContent className={styles.loadingContent}>
              <CircularProgress size={60} className={styles.loadingSpinner} />
              <Typography variant="h6" className={styles.loadingTitle}>
                Analizando tu sitio web...
              </Typography>
              <Typography variant="body2" className={styles.loadingDescription}>
                Estamos revisando la estructura de tu sitio y categorizando las URLs. Esto puede tomar unos momentos.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {analysisError && !isAnalyzing && (
          <Card className={styles.errorCard}>
            <CardContent className={styles.cardContent}>
              <Typography variant="h6" color="error" gutterBottom>
                Error en el Análisis
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {analysisError}
              </Typography>
              <Button variant="outlined" onClick={() => setAnalysisError(null)} color="primary">
                Intentar de Nuevo
              </Button>
            </CardContent>
          </Card>
        )}

        {!isAnalyzing && !analysisError && (
          <Card className={styles.setupCard}>
            <CardContent className={styles.cardContent}>
              <Box className={styles.stepIcon}>
                <LanguageIcon fontSize="large" color="primary" />
              </Box>

              <Typography variant="h5" component="h2" className={styles.stepTitle}>
                URL de tu Concesionario
              </Typography>

              <Typography variant="body1" className={styles.stepDescription}>
                Ingresa la URL principal de tu sitio web. Analizaremos la estructura y categorizaremos automáticamente
                tus URLs de inventario.
              </Typography>

              <form
                onSubmit={(e) => {
                  handleSubmit(e).catch(() => {});
                }}
                className={styles.form}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  label="URL de tu concesionario"
                  placeholder="https://miconcesionario.com"
                  value={companyUrl}
                  onChange={handleUrlChange}
                  error={!!errors.companyUrl}
                  helperText={errors.companyUrl || 'Ejemplo: https://miconcesionario.com'}
                  disabled={isAnalyzing}
                  InputProps={{
                    startAdornment: <LanguageIcon color="action" style={{ marginRight: 8 }} />,
                  }}
                  className={styles.urlInput}
                />

                <Box className={styles.actionButtons}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard')}
                    disabled={isAnalyzing}
                    className={styles.skipButton}
                  >
                    Saltar por ahora
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isAnalyzing || !companyUrl.trim()}
                    startIcon={isAnalyzing ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon />}
                    className={styles.continueButton}
                  >
                    {isAnalyzing ? 'Analizando...' : 'Analizar Sitio Web'}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        )}

        {!isAnalyzing && !analysisError && (
          <Box className={styles.benefitsSection}>
            <Typography variant="h6" className={styles.benefitsTitle}>
              ¿Qué analizaremos?
            </Typography>
            <Box className={styles.benefitsList}>
              <Box className={styles.benefitItem}>
                <CheckCircleOutlineIcon color="primary" className={styles.benefitIcon} />
                <Typography variant="body2">Identificar automáticamente URLs de autos nuevos y usados</Typography>
              </Box>
              <Box className={styles.benefitItem}>
                <CheckCircleOutlineIcon color="primary" className={styles.benefitIcon} />
                <Typography variant="body2">Detectar marcas disponibles y estructura de navegación</Typography>
              </Box>
              <Box className={styles.benefitItem}>
                <CheckCircleOutlineIcon color="primary" className={styles.benefitIcon} />
                <Typography variant="body2">Estimar la cantidad de vehículos en inventario</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </div>
    </div>
  );
};

export default SetupOnboarding;
